import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { useBookingGroups, useNavigation, useProvider } from '~/Hooks';
import { EventType } from '~/Models/DTO/Event.model';
import { EventGroupDefaultValue } from '~/Models/DTO/EventGroup.model';
import { selectActiveTrainerList, selectLocationList } from '~/store/provider';
import { ExtendedEventGroup } from '../ExtendedEventGroup.model';
import { CategoryMap } from '../helpers';

export const useEventCreateInitializer = () => {
  const { currentProvider, isUsingThePartnerTool } = useProvider();
  const { bookingGroupsList } = useBookingGroups();
  const { getParams } = useNavigation();

  const { state } = useLocation<{ eventGroup: Partial<ExtendedEventGroup> }>();
  const trainerList = useSelector(selectActiveTrainerList);
  const locationList = useSelector(selectLocationList);

  const { groupId: id, eventType } = getParams<{
    groupId: string;
    eventType: EventType;
  }>();

  const categoryId = useMemo(() => {
    if (isUsingThePartnerTool) {
      return null;
    }
    return CategoryMap.get(`${currentProvider?.providerCategory?.id || 1}`);
  }, [isUsingThePartnerTool, currentProvider]);

  const [eventGroup, setEventGroup] = useState<ExtendedEventGroup>({
    name: '',
    type: eventType,
    seats: 5,
    public: true,
    isUSCIntegrationEnabled: true,
    livestream: false,
    publicCancellationEnabled: true,
    color: PRIMARY_BLUE,
    categoryId,
    trainerIds: trainerList.length ? [trainerList?.[0]?.trainerId] : [],
    locationId: '',
    bookingGroupIds: bookingGroupsList?.length ? [bookingGroupsList?.[0]?.uuid] : [],
    description: '',
    timeSeries: [],
    waitingListEnabled: false,
    bookingWindowOpen: false,
    hasAccessToZoom: false,
    isZoomSelected: false,
    isZoomWebinar: false,
    liveStreamEnabled: locationList?.[0]?.liveStreamEnabled || false,
    publicCancellationRefundEndAsNumber: EventGroupDefaultValue.publicCancellationRefundEndAsNumber,
    publicCancellationRefundEndType: EventGroupDefaultValue.publicCancellationRefundEndType,
    publicBookingWindowStartType: EventGroupDefaultValue.publicBookingWindowStartType,
    publicBookingWindowEndType: EventGroupDefaultValue.publicBookingWindowEndType,
    publicCancellationWindowOpen: false,
    ...state?.eventGroup,
  } as ExtendedEventGroup);

  return {
    eventGroup,
    setEventGroup,
    eventType,
    id,
    locationList,
    trainerList,
    liveStreamEnabled: locationList?.[0]?.liveStreamEnabled || false,
  };
};
